import React from "react";
import { Alert, Box, Button, Card, Column, Columns, Stack, Text, TextField, Tiles } from "braid-design-system";
import { useAuth } from "../contexts/AuthProvider";
import {
    getCashVarianceReport,
    getServerDateTime,
    getSignOffPaymodes,
    getUserSignOnRecords,
    ServerDateTime,
    SignOffDeclaration,
    SignOffPaymode,
    signOffCurrentUser,
    UserSignOnRecord,
} from "../services/signOnService";
import { sanitizeNumericString } from "../utils/numericInput";
import { printCashVarianceReport } from "./signoff/cashVarianceReportPrint";

// One input per payment mode; card modes get EDC sales + EDC refunds
type CountField = { key: string; label: string; payCode: string; part: "amount" | "refund" };

const toCountFields = (paymodes: SignOffPaymode[]): CountField[] =>
    paymodes.flatMap((mode): CountField[] =>
        mode.has_refund
            ? [
                { key: `${mode.pay_code}:amount`, label: `${mode.description} Sales (EDC)`, payCode: mode.pay_code, part: "amount" },
                { key: `${mode.pay_code}:refund`, label: `${mode.description} Refunds (EDC)`, payCode: mode.pay_code, part: "refund" },
            ]
            : [{
                key: `${mode.pay_code}:amount`,
                label: mode.category === "cash" ? `${mode.description} Counted` : mode.description,
                payCode: mode.pay_code,
                part: "amount",
            }]
    );

const SignOff: React.FC = () => {
    const { user } = useAuth();
    const userName = user?.user_name || "-";

    const [serverDateTime, setServerDateTime] = React.useState<ServerDateTime | null>(null);
    const [clientNow, setClientNow] = React.useState(() => new Date());
    const [signOffRecord, setSignOffRecord] = React.useState<UserSignOnRecord | null>(null);
    const [stationId, setStationId] = React.useState<string | null>(null);
    // null while the sign-on record is still loading
    const [isSignedOn, setIsSignedOn] = React.useState<boolean | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    // Physical totals counted by the cashier (blind count - system totals are only on the report)
    const [paymodes, setPaymodes] = React.useState<SignOffPaymode[] | null>(null);
    const [paymodeError, setPaymodeError] = React.useState<string | null>(null);
    const [counts, setCounts] = React.useState<Record<string, string>>({});
    const [countErrors, setCountErrors] = React.useState<Record<string, string | undefined>>({});
    // Shift closed by this sign-off; null reprints the user's latest closed shift
    const [reportShiftId, setReportShiftId] = React.useState<number | null>(null);
    const [isPrinting, setIsPrinting] = React.useState(false);
    const [printError, setPrintError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const timer = setInterval(() => setClientNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    React.useEffect(() => {
        const fetchServerDateTime = async () => {
            try {
                const dt = await getServerDateTime();
                setServerDateTime(dt);
            } catch {
                // Silently fail - will use fallback from service
            }
        };
        fetchServerDateTime();
    }, []);

    React.useEffect(() => {
        const userNameToCheck = user?.user_name;
        if (!userNameToCheck) {
            return;
        }
        const fetchStationId = async () => {
            try {
                const record = await getUserSignOnRecords(userNameToCheck);
                if (record?.stationid != null) {
                    setStationId(String(record.stationid));
                }
                setIsSignedOn(record?.signon === 1);
            } catch {
                // Silently fail - station ID will show as "-"
                setIsSignedOn(false);
            }
        };
        fetchStationId();
    }, [user?.user_name]);

    React.useEffect(() => {
        if (!isSignedOn || paymodes !== null) {
            return;
        }
        getSignOffPaymodes()
            .then(setPaymodes)
            .catch((error: any) => setPaymodeError(error.message || "Failed to load payment modes"));
    }, [isSignedOn, paymodes]);

    const countFields = React.useMemo(() => toCountFields(paymodes ?? []), [paymodes]);

    const clientDate = React.useMemo(() => {
        const yyyy = String(clientNow.getFullYear());
        const mm = String(clientNow.getMonth() + 1).padStart(2, "0");
        const dd = String(clientNow.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    }, [clientNow]);

    const clientTime = React.useMemo(() => {
        const hh = String(clientNow.getHours()).padStart(2, "0");
        const min = String(clientNow.getMinutes()).padStart(2, "0");
        const ss = String(clientNow.getSeconds()).padStart(2, "0");
        return `${hh}:${min}:${ss}`;
    }, [clientNow]);

    const handleCountChange = (key: string, value: string) => {
        setCounts((prev) => ({ ...prev, [key]: sanitizeNumericString(value) }));
        setCountErrors((prev) => ({ ...prev, [key]: undefined }));
    };

    const handlePrintReport = async (shiftId: number | null) => {
        setIsPrinting(true);
        setPrintError(null);
        try {
            const report = await getCashVarianceReport(shiftId);
            printCashVarianceReport(report, setPrintError);
        } catch (error: any) {
            setPrintError(error.message || "Failed to print the cash variance report");
        } finally {
            setIsPrinting(false);
        }
    };

    const handleSignOff = async () => {
        const errors: Record<string, string> = {};
        for (const { key, label } of countFields) {
            const value = (counts[key] ?? "").trim();
            if (value === "" || !Number.isFinite(Number(value))) {
                errors[key] = `${label} is required`;
            }
        }
        if (Object.keys(errors).length > 0) {
            setCountErrors(errors);
            return;
        }
        const declarations: SignOffDeclaration[] = (paymodes ?? []).map((mode) => ({
            pay_code: mode.pay_code,
            amount: Number(counts[`${mode.pay_code}:amount`]),
            refund_amount: mode.has_refund ? Number(counts[`${mode.pay_code}:refund`]) : null,
        }));

        setIsLoading(true);
        setErrorMessage(null);
        setPrintError(null);
        setSignOffRecord(null);
        try {
            if (!user?.user_name) {
                setErrorMessage("User not found");
                return;
            }
            try {
                const dt = await getServerDateTime();
                setServerDateTime(dt);
            } catch {
                // ignore
            }
            const updated = await signOffCurrentUser(user.user_name, declarations);
            if (!updated) {
                setErrorMessage("Unable to sign off user");
                return;
            }
            setSignOffRecord(updated);
            setIsSignedOn(updated.signon === 1);
            if (updated.stationid != null) {
                setStationId(String(updated.stationid));
            }
            setCounts({});
            window.dispatchEvent(new CustomEvent('webpos:signon-status-changed'));
            if (updated.shift_log_id != null) {
                setReportShiftId(updated.shift_log_id);
                await handlePrintReport(updated.shift_log_id);
            }
        } catch (error: any) {
            setErrorMessage(error.message || "Failed to sign off user");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Box padding="medium">
                <Card>
                    <Box padding="medium">
                        <Stack space="medium">
                            <Text weight="strong" size="large">Cashier Sign Off</Text>

                            <Columns space="medium" collapseBelow="tablet">
                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">User Name</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <div style={{ textAlign: "left" }}>
                                                <Text>{userName}</Text>
                                            </div>
                                        </Box>
                                    </Stack>
                                </Column>

                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">Counter</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <div style={{ textAlign: "left" }}>
                                                <Text>{stationId ?? "-"}</Text>
                                            </div>
                                        </Box>
                                    </Stack>
                                </Column>

                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">Sign Off Date</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <Text>{serverDateTime?.date || clientDate}</Text>
                                        </Box>
                                    </Stack>
                                </Column>

                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">Sign Off Time</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <Text>{serverDateTime?.time || clientTime}</Text>
                                        </Box>
                                    </Stack>
                                </Column>
                            </Columns>

                            {isSignedOn ? (
                                <Stack space="small">
                                    <Text weight="strong">Physical Count</Text>
                                    <Text tone="secondary" size="small">
                                        Count the drawer and enter the physical totals. System totals and the variance are printed on the Daily Cash Reconciliation after sign off.
                                    </Text>
                                    {paymodeError ? (
                                        <Alert tone="critical">
                                            <Text>{paymodeError}</Text>
                                        </Alert>
                                    ) : null}
                                    <Tiles space="medium" columns={{ mobile: 1, tablet: 3 }}>
                                        {countFields.map(({ key, label }) => (
                                            <TextField
                                                key={key}
                                                id={`signoff-count-${key}`}
                                                label={label}
                                                value={counts[key] ?? ""}
                                                onChange={(e) => handleCountChange(key, e.currentTarget.value)}
                                                tone={countErrors[key] ? "critical" : undefined}
                                                message={countErrors[key] || undefined}
                                                inputMode="decimal"
                                                placeholder="0.00"
                                            />
                                        ))}
                                    </Tiles>
                                </Stack>
                            ) : null}

                            <Box display="flex" gap="small" flexWrap="wrap">
                                <div style={{ width: "140px" }}>
                                    <Button
                                        variant="solid"
                                        tone={isSignedOn && paymodes !== null ? "brandAccent" : "neutral"}
                                        onClick={isSignedOn && paymodes !== null && !isLoading ? handleSignOff : undefined}
                                        loading={isLoading}
                                    >
                                        Sign Off
                                    </Button>
                                </div>
                                {isSignedOn === false ? (
                                    <Button
                                        variant="ghost"
                                        tone="formAccent"
                                        onClick={isPrinting ? undefined : () => handlePrintReport(reportShiftId)}
                                        loading={isPrinting}
                                    >
                                        {reportShiftId != null ? "Reprint Daily Cash Reconciliation" : "Print Last Daily Cash Reconciliation"}
                                    </Button>
                                ) : null}
                            </Box>

                            {isSignedOn === false && signOffRecord === null ? (
                                <Alert tone="info">
                                    <Text>You are not signed on to any counter.</Text>
                                </Alert>
                            ) : null}

                            {errorMessage ? (
                                <Alert tone="critical">
                                    {errorMessage && <Text>{errorMessage}</Text>}
                                </Alert>
                            ) : null}

                            {printError ? (
                                <Alert tone="caution">
                                    <Text>{printError}</Text>
                                </Alert>
                            ) : null}

                            {signOffRecord !== null && signOffRecord.signon === 0 ? (
                                <Alert tone="positive">
                                    <div style={{ textAlign: "left" }}>
                                        <Text>
                                            {signOffRecord.shift_log_id != null
                                                ? "Login user sign off completed. Daily Cash Reconciliation sent to the printer."
                                                : "Login user sign off completed. No shift record was found, so there is no Daily Cash Reconciliation for this sign off."}
                                        </Text>
                                    </div>
                                </Alert>
                            ) : null}
                        </Stack>
                    </Box>
                </Card>
            </Box>
        </>
    );
};

export default SignOff;

